// src/modules/care-gaps-2/components/CareGapTypeDashboard.jsx
import * as React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Autocomplete,
  Chip,
  Pagination,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

import careGapTypeAll from "../DataTest/CaregapTypeAll.json";

const BARS_PER_PAGE = 15;

function buildBaseData() {
  return (careGapTypeAll || [])
    .filter((row) => typeof row?.total === "number")
    .map((row) => {
      const desc =
        row?.careGapTypeDescription ??
        row?.description ??
        row?.displayName ??
        row?.careGapType ??
        "";

      return {
        ...row,
        label: desc, // ✅ descripción para el eje X
      };
    })
    .filter((row) => !!row?.label);
}

function sumTotal(data) {
  return (data || []).reduce((acc, row) => acc + (row?.total ?? 0), 0);
}

function truncateLabel(value, max = 26) {
  const s = String(value ?? "");
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
}

/**
 * Props:
 * - onSelectMeasure?: (row) => void
 */
export default function CareGapTypeDashboard({ onSelectMeasure }) {
  const [search, setSearch] = React.useState("");
  const [selectedOption, setSelectedOption] = React.useState(null);
  const [page, setPage] = React.useState(1);

  const baseData = React.useMemo(() => buildBaseData(), []);

  const options = React.useMemo(
    () =>
      baseData.map((row) => ({
        label: row.label,       // descripción
        value: row.careGapType, // código si existe
      })),
    [baseData]
  );

  const filteredData = React.useMemo(() => {
    let data = [...baseData];

    if (selectedOption) {
      data = data.filter((row) =>
        row?.careGapType
          ? row.careGapType === selectedOption.value
          : row.label === selectedOption.label
      );
    } else if (search.trim()) {
      const term = search.toLowerCase();
      data = data.filter((row) => (row.label || "").toLowerCase().includes(term));
    }

    data.sort((a, b) => (b.total ?? 0) - (a.total ?? 0));
    return data;
  }, [baseData, search, selectedOption]);

  const totalGaps = React.useMemo(() => sumTotal(filteredData), [filteredData]);
  const totalPages = Math.max(1, Math.ceil(filteredData.length / BARS_PER_PAGE));

  React.useEffect(() => setPage(1), [search, selectedOption]);

  const pageData = React.useMemo(() => {
    const start = (page - 1) * BARS_PER_PAGE;
    return filteredData.slice(start, start + BARS_PER_PAGE);
  }, [filteredData, page]);

  const handleClearSelection = () => {
    setSelectedOption(null);
    setSearch("");
  };

  const handlePageChange = (_e, value) => setPage(value);

  // ✅ Click garantizado: Recharts llama onClick con (data, index)
  const handleBarClick = (data) => {
    const row = data?.payload;
    if (!row) return;

    onSelectMeasure?.(row);

    // sincroniza UI del dashboard
    setSelectedOption({
      label: row.label,
      value: row.careGapType,
    });
  };

  return (
    <Paper sx={{ p: 2.25, mb: 3, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" fontWeight={800}>
            Care Gaps Overview by Measure
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sorted by <b>Total</b> (desc). Click a bar to filter the worklist by
            measure <b>description</b>.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <TextField
            label="Search by measure (description)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
          />

          <Autocomplete
            sx={{ minWidth: 320 }}
            options={options}
            value={selectedOption}
            onChange={(_event, newValue) => setSelectedOption(newValue)}
            getOptionLabel={(option) => option?.label || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Go to specific measure"
                placeholder="Start typing a description..."
              />
            )}
            clearOnEscape
            isOptionEqualToValue={(opt, val) => opt?.label === val?.label}
          />

          <Chip label="Clear" variant="outlined" onClick={handleClearSelection} />
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip size="small" label={`Measures: ${filteredData.length}`} />
          <Chip size="small" label={`Total gaps (filtered): ${totalGaps}`} />
          <Chip size="small" variant="outlined" label="Sort: Total ↓" />
          <Chip size="small" label={`Page: ${page} / ${totalPages}`} />
        </Stack>

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Box sx={{ width: "100%", height: 360 }}>
            <ResponsiveContainer>
              <BarChart data={pageData} margin={{ top: 16, right: 24, left: 0, bottom: 90 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={90}
                  tick={{ fontSize: 10 }}
                  tickMargin={10}
                  tickFormatter={(v) => truncateLabel(v, 26)}
                />
                <YAxis />
                <RechartsTooltip
                  labelFormatter={(label) => `Measure: ${label}`}
                  formatter={(value, name) => [
                    value,
                    name.charAt(0).toUpperCase() + name.slice(1),
                  ]}
                />
                <Legend />

                {/* ✅ CLICK EN LAS BARRAS */}
                <Bar
                  dataKey="open"
                  stackId="status"
                  name="Open"
                  fill="#e53935"
                  cursor="pointer"
                  onClick={handleBarClick}
                />
                <Bar
                  dataKey="closed"
                  stackId="status"
                  name="Closed"
                  fill="#43a047"
                  cursor="pointer"
                  onClick={handleBarClick}
                />
                <Bar
                  dataKey="submitted"
                  stackId="status"
                  name="Submitted"
                  fill="#fdd835"
                  cursor="pointer"
                  onClick={handleBarClick}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">
            Showing measures{" "}
            <b>
              {filteredData.length === 0 ? 0 : (page - 1) * BARS_PER_PAGE + 1}-
              {Math.min(page * BARS_PER_PAGE, filteredData.length)}
            </b>{" "}
            of <b>{filteredData.length}</b>
          </Typography>

          <Pagination
            size="small"
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Stack>
      </Stack>
    </Paper>
  );
}
